// @flow

import _ from 'lodash';
import Collapse from 'react-collapse';
import React from 'react';
import Translate from 'client/components/translate';
import type { FieldProps } from 'redux-form';

import { convertToNumber, getFormattedDob } from 'client/utils/format';
import { getAge } from 'client/utils/member';
import { isValidDate } from 'client/utils/validate';
import { parseNumber } from 'client/utils/finance';
import Button from 'client/components/button/button';
import Card from 'client/components/card/card';
import InputCurrency from 'client/components/form/inputCurrency';
import InputDate from 'client/components/form/inputDate';
import InputWeight from 'client/components/form/inputWeight';
import Loader from 'client/components/loader';
import PlanCard from 'client/components/plan/card/planCard';
import RequiredField from 'client/components/form/requiredField';
import Ribbon from 'client/components/plan/card/ribbon';
import Segment from 'client/components/form/segment';
import Select from 'client/components/form/select';
import type { LadderGenderType } from 'client/actions/ladder';

import { trackLifeQuote, trackQuoteBtnClick } from 'routes/life/util/tracking';
import {
  GET_LIFE_QUOTE,
  SELECT_LIFE_QUOTE,
  YES_NO_OPTIONS,
  GENDER_OPTIONS,
} from 'routes/life/constant';
import { getLadderFormattedGender, getAgeValidator } from 'routes/life/util';
import { BOOLEAN_VALIDATION } from 'routes/marketplace/utils/validations';

import type { QuotePolicyType } from 'web-core/types/ladder';

import '../styles/life.scss';

// For retrieving correct translation copy for each plan
const planMapping = {
  // $FlowFixMe, 'Non-string literal property keys not supported'
  0: 'first',
  // $FlowFixMe
  1: 'second',
  // $FlowFixMe
  2: 'third',
  // $FlowFixMe
  3: 'fourth',
  // $FlowFixMe
  4: 'fifth',
};

// For implementing the correct styling for each plan card ribbon
const ribbonMapping = {
  // $FlowFixMe, 'Non-string literal property keys not supported'
  0: 'gtg', // dark green
  // $FlowFixMe
  1: 'purple', // purple
  // $FlowFixMe
  2: 'green', // green
  // $FlowFixMe
  3: 'waiting', // orange
  // $FlowFixMe
  4: 'covered', // blue
};

const writeQuoteSubmitToAmplitude = ({
  coverage,
  dob,
  familyHistory,
  gender,
  height,
  householdIncome,
  householdSize,
  monthlyCost,
  planUrl,
  termYears,
  tobacco,
  weight,
}) => {
  const formattedDob = getFormattedDob(dob, 'MM-DD-YYYY');
  const age = getAge(formattedDob);
  const userInfo = {
    age,
    coverage,
    familyHistory,
    gender,
    height,
    householdIncome,
    householdSize,
    monthlyCost,
    planUrl,
    termYears,
    tobacco,
    weight,
  };

  trackLifeQuote(SELECT_LIFE_QUOTE, userInfo);
};

const writeQuoteToAmplitude = ({
  dob,
  familyHistory,
  gender,
  height,
  householdIncome,
  householdSize,
  lifeQuotes,
  tobacco,
  weight,
}) => {
  const plansInfo = lifeQuotes.map(quote =>
    ['coverage', 'monthly_premium', 'term'].map(key => quote[key]).join(','));
  const age = getAge(dob); // dob is YYYY-MM-DD
  const userInfo = {
    age,
    familyHistory,
    height,
    householdIncome,
    householdSize,
    tobacco,
    weight,
    gender: _.capitalize(gender[0]), // first letter to be consistent with writeQuoteSubmitToAmplitude
    ...plansInfo,
  };

  trackLifeQuote(GET_LIFE_QUOTE, userInfo);
};

const quotesDivId = 'quotes';

const GENDER_VALIDATION = [
  val => (val === 'M' || val === 'F' ? undefined : { path: 'common.form.error.required' }),
];

const DOB_VALIDATION = [_.partial(isValidDate, _, 10), getAgeValidator()];

type Props = {
  benefitsSection: React$Element<'section'>,
  createOrUpdateLifePlan: Function,
  fetchLadderPlanUrl: Function,
  fetchLadderQuote: Function,
  formData: {
    dob: '' | string,
    familyHistory: boolean,
    gender: 'M' | 'F' | null,
    height: '' | number,
    householdIncome: string,
    tobacco: boolean,
    weight: '' | number,
    coverage: '' | number,
    term: '' | number,
  },
  handleSubmit: (eventOrSubmit: any) => void | Promise<*>,
  householdIncome: string | 'null', // Was set up as string null for Amplitude tracking
  householdSize: string | 'null', // Was set up as string null for Amplitude tracking
  isLoggedIn: boolean,
  putMembers: Function,
  router: {
    replace: Function,
    location: {
      query: {
        dob: string,
        familyHistory: 'true' | 'false',
        gender: LadderGenderType, // post-getLadderFormattedGender transform
        height: string,
        householdIncome: string,
        tobacco: 'true' | 'false',
        weight: string,
        coverage: string,
        term: string,
      },
      pathname: string,
    },
  },
  translations: Object,
  userId: string,
  uuid: string,
};

type State = {
  hasMadeFirstFetch: boolean,
  isFetching: boolean,
  lifeQuotes: Array<QuotePolicyType>, // TODO: Flow fix
  lifeQuoteError: Object | null, // Not defined and/or tested
  lifeQuoteId: string,
  ineligible: boolean,
};

class LifeForm extends React.Component<Props, State> {
  state = {
    hasMadeFirstFetch: false,
    isFetching: false,
    lifeQuotes: [],
    lifeQuoteError: null,
    lifeQuoteId: '',
    ineligible: false,
  };

  // If redirecting from account creation/login, we generate quotes from the URL params
  // Example: http://www.stridehealth.com/life?dob=1991-01-02&gender=Male&tobacco=true&healthStatus=3
  componentDidMount = () => {
    const { router } = this.props;
    const {
      query: {
        coverage,
        dob,
        familyHistory,
        gender,
        height,
        householdIncome,
        term,
        tobacco,
        weight,
      },
      pathname,
    } = router.location;

    if (dob && gender && tobacco) {
      const options = {
        dob,
        familyHistory: familyHistory === 'true',
        height,
        householdIncome,
        gender,
        tobacco: tobacco === 'true',
        weight,
        term,
        coverage,
      };
      this.handleQuoteFetch(options).then(() => {
        router.replace(`${pathname}`); // Remove param values
      });
    }
  };

  shouldComponentUpdate(nextProps: Object, nextState: Object) {
    const {
      dob,
      familyHistory,
      gender,
      height,
      householdIncome,
      tobacco,
      weight,
    } = this.props.formData;
    const {
      dob: nextDob,
      familyHistory: nextFamilyHistory,
      gender: nextGender,
      height: nextHeight,
      householdIncome: nextHouseholdIncome,
      tobacco: nextTobacco,
      weight: nextWeight,
    } = nextProps.formData;

    const { isFetching, lifeQuotes, lifeQuoteError } = this.state;
    const {
      isFetching: nextIsFetching,
      lifeQuotes: nextLifeQuote,
      lifeQuoteError: nextLifeQuoteError,
    } = nextState;

    if (
      dob !== nextDob ||
      familyHistory !== nextFamilyHistory ||
      gender !== nextGender ||
      height !== nextHeight ||
      householdIncome !== nextHouseholdIncome ||
      tobacco !== nextTobacco ||
      weight !== nextWeight
    ) {
      return true;
    }

    if (
      isFetching !== nextIsFetching ||
      lifeQuotes.length !== nextLifeQuote.length ||
      lifeQuoteError !== nextLifeQuoteError
    ) {
      return true;
    }

    return false;
  }

  handleQuoteClick = ({
    coverage,
    monthly_premium: monthlyCost,
    term: termYears,
  }: QuotePolicyType) => {
    const {
      createOrUpdateLifePlan,
      fetchLadderPlanUrl,
      formData: { dob, familyHistory, gender, height, householdIncome, tobacco, weight },
      householdSize,
      putMembers,
      uuid,
      userId,
    } = this.props;

    const { lifeQuoteId } = this.state;

    putMembers([
      {
        id: uuid,
        gender,
        tobacco,
        dob: getFormattedDob(dob, 'MM-DD-YYYY'),
        type: 'primary',
      },
    ]);

    const newWindow = window.open('', '_blank');

    fetchLadderPlanUrl(lifeQuoteId, {
      date_of_birth: getFormattedDob(dob, 'MM-DD-YYYY'),
      external_id: userId,
      family_history: familyHistory,
      height_inches: height,
      household_income: parseNumber(householdIncome),
      smoker: tobacco,
      term: termYears,
      weight_pounds: weight,
    }).then(({ url: planUrl }) => {
      createOrUpdateLifePlan({
        coverage,
        familyHistory,
        householdIncome: parseNumber(householdIncome),
        monthlyCost,
        planUrl,
        termYears,
        height,
        weight,
      });
      writeQuoteSubmitToAmplitude({
        coverage,
        dob,
        familyHistory,
        gender,
        height,
        householdIncome,
        householdSize,
        monthlyCost,
        planUrl,
        termYears,
        tobacco,
        weight,
      });
      newWindow.location.href = planUrl;
    });
  };

  handleQuoteFetch = ({
    dob,
    familyHistory,
    gender,
    height,
    householdIncome,
    tobacco,
    weight,
    term,
    coverage,
  }: {
    dob: string,
    familyHistory: boolean,
    gender: LadderGenderType,
    householdIncome: string,
    height: string | number,
    tobacco: boolean,
    weight: string | number,
    term: string | number,
    coverage: string | number,
  }) => {
    const { fetchLadderQuote, householdSize } = this.props;

    return fetchLadderQuote({
      dob,
      familyHistory,
      gender,
      height: convertToNumber(height),
      householdIncome: parseNumber(householdIncome),
      tobacco,
      weight: convertToNumber(weight),
      term: convertToNumber(term),
      coverage: convertToNumber(coverage),
    })
      .then((res) => {
        const { quotes } = res;
        // moving the selected term policy to the first
        const findTermQuote = _.remove(quotes, q => q.term === convertToNumber(term));
        const filteredQuotes = [...findTermQuote, ...quotes];

        if (_.isEmpty(filteredQuotes)) {
          this.setState({ ineligible: true });
        }

        this.setState({
          lifeQuotes: filteredQuotes,
          lifeQuoteId: res.id,
          hasMadeFirstFetch: true,
          isFetching: false,
        });
        writeQuoteToAmplitude({
          dob,
          familyHistory,
          gender,
          height,
          householdIncome,
          householdSize,
          lifeQuotes: filteredQuotes,
          tobacco,
          weight,
        });
      })
      .then(() => {
        // Page jumps to quote section after retrieval
        setTimeout(() => {
          const quotesDiv = document.getElementById(quotesDivId);
          if (quotesDiv) {
            quotesDiv.scrollIntoView();
          }
        }, 500);
      })
      .catch((error) => {
        this.setState({ lifeQuoteError: error, isFetching: false });
      });
  };

  handleGetQuote = () => {
    const { isLoggedIn, formData, router } = this.props;
    const {
      dob: formDataDob,
      familyHistory,
      gender: formDataGender,
      height,
      householdIncome,
      tobacco,
      weight,
      coverage,
      term,
    } = formData;
    const { pathname } = router.location;
    const gender = getLadderFormattedGender(formDataGender);
    const dob = getFormattedDob(formDataDob, 'MM-DD-YYYY');

    trackQuoteBtnClick();

    if (!_.isBoolean(tobacco) || !gender || !dob) {
      // For type-checking validation
    } else if (isLoggedIn === false) {
      this.setState({ isFetching: true });
      const familyHistoryStatus = _.toString(familyHistory);
      const h = _.toString(height);
      const tobaccoStatus = _.toString(tobacco);
      const w = _.toString(weight);
      const c = _.toString(coverage);
      const t = _.toString(term);
      const stringURI = encodeURIComponent(
        `/life${pathname}?dob=${dob}&familyHistory=${familyHistoryStatus}&gender=${gender}&height=${h}&householdIncome=${householdIncome}&tobacco=${tobaccoStatus}&weight=${w}&coverage=${c}&term=${t}`,
      );

      setTimeout(() => window.location.replace(`/account/create?&redirect=${stringURI}`), 1000);
    } else {
      this.setState({
        lifeQuotes: [],
        lifeQuoteError: null,
        isFetching: true,
        ineligible: false,
      });
      this.handleQuoteFetch({
        dob,
        familyHistory,
        gender,
        height,
        householdIncome,
        tobacco,
        weight,
        term,
        coverage,
      });
    }
  };

  renderDob = ({ input, meta: { touched, error } }: FieldProps) => (
    <InputDate
      {...input}
      id={input.name}
      placeholder="MM/DD/YYYY"
      error={touched && error}
      label={{
        path: 'life.label.dob',
      }}
      changeHandler={value => input.onChange(value)} />
  );

  renderDob: FieldProps => React$Element<'div'>;

  renderHouseholdIncome = ({ input, meta: { touched, error } }: FieldProps) => (
    <InputCurrency
      {...input}
      id={input.name}
      error={touched && error}
      label={{
        path: 'life.form.income',
      }}
      changeHandler={value => input.onChange(value)} />
  );

  renderHouseholdIncome: FieldProps => React$Element<'div'>;

  renderWeight = ({ input, meta: { touched, error } }: FieldProps) => (
    <InputWeight
      {...input}
      id={input.name}
      error={touched && error}
      label={{
        path: 'life.form.weight',
      }}
      changeHandler={value => input.onChange(value)} />
  );

  renderWeight: FieldProps => React$Element<'div'>;

  renderHeightSelect = ({ input, meta: { touched, error } }: FieldProps) => {
    const { translations } = this.props;
    const options = _.get(translations, 'life.form.height.options', []);
    const placeholder = _.get(translations, 'life.form.height.label');

    return (
      <Select
        {...input}
        id={input.name}
        options={options}
        error={touched && error}
        label={{ path: 'life.form.height.label' }}
        isRequired
        placeholder={placeholder}
        changeHandler={input.onChange} />
    );
  };

  renderHeightSelect: FieldProps => React$Element<'div'>;

  renderGenderSegment = ({ input, meta: { touched, error } }: FieldProps) => {
    const { translations } = this.props;
    const options = _.get(translations, 'life.form.gender.options', GENDER_OPTIONS);

    return (
      <Segment
        {...input}
        id={input.name}
        options={options}
        error={touched && error}
        clickHandler={input.onChange} />
    );
  };

  renderGenderSegment: FieldProps => React$Element<'div'>;

  renderSmokingSegment = ({ input, meta: { touched, error } }: FieldProps) => {
    const { translations } = this.props;
    const options = _.get(translations, 'life.form.tobacco.options', YES_NO_OPTIONS);

    return (
      <Segment
        {...input}
        id={input.name}
        error={touched && error}
        options={options}
        clickHandler={input.onChange} />
    );
  };

  renderSmokingSegment: FieldProps => React$Element<'div'>;

  renderFamilyHistorySegment = ({ input, meta: { touched, error } }: FieldProps) => {
    const { translations } = this.props;
    const options = _.get(translations, 'life.form.familyHistory.options', YES_NO_OPTIONS);

    return (
      <Segment
        {...input}
        id={input.name}
        error={touched && error}
        options={options}
        clickHandler={input.onChange} />
    );
  };

  renderFamilyHistorySegment: FieldProps => React$Element<'div'>;

  renderSubmitButton = () => (
    <div className="text-center" id={quotesDivId}>
      <Button className="primary ede-cta gtm-life-quote" type="submit">
        <Translate
          className="gtm-life-quote"
          path={this.state.hasMadeFirstFetch ? 'life.recalculate' : 'life.calculate'} />
      </Button>
    </div>
  );

  renderCoverageSelect = ({ input, meta: { touched, error } }: FieldProps) => {
    const { translations } = this.props;
    const options = _.get(translations, 'life.form.coverage.options', []);

    return (
      <Select
        {...input}
        id={input.name}
        options={options}
        error={touched && error}
        label={{ path: 'life.form.coverage.label' }}
        isRequired
        changeHandler={input.onChange} />
    );
  };

  renderCoverageSelect: FieldProps => React$Element<'div'>;

  renderTermSelect = ({ input, meta: { touched, error } }: FieldProps) => {
    const { translations } = this.props;
    const options = _.get(translations, 'life.form.term.options', []);

    return (
      <Select
        {...input}
        id={input.name}
        options={options}
        error={touched && error}
        label={{ path: 'life.form.term.label' }}
        isRequired
        changeHandler={input.onChange} />
    );
  };

  renderTermSelect: FieldProps => React$Element<'div'>;

  render() {
    const { benefitsSection, handleSubmit } = this.props;
    const { lifeQuotes, lifeQuoteError, isFetching, ineligible } = this.state;

    return (
      <div className="life-form-parent">
        <div className="life-form" id="quotes-top">
          <div className="center-col center-col-shift">
            <div className="header-image margin-bottom">
              <div className="pad-bottom-2 text-center form-image">
                <img
                  className="hide-for-small-only"
                  alt="family"
                  src="https://images.contentful.com/heg7gyzix6dq/2aPsO8VUxK8gsgQIyOu8AU/ff9a6233d23c6e71e20180fb9d6b46c7/family-life.svg" />
                <img
                  className="show-for-small-only"
                  alt="family"
                  src="https://images.contentful.com/heg7gyzix6dq/5OtkQkMHNCmkyWgAEqmm4Q/d9f10f9f10eb2cd710fbdd538674dbed/family.svg" />
              </div>
              <h3 className="pad-top-2 text-center">
                <Translate path="life.form.calculate" />
              </h3>
            </div>
            <form onSubmit={handleSubmit(this.handleGetQuote)}>
              <div className="fields">
                <div className="actions form-actions gender">
                  <div className="flex-row-text hide-for-medium">
                    <Translate path="life.form.gender.label" />
                  </div>
                  <RequiredField
                    name="gender"
                    component={this.renderGenderSegment}
                    validate={GENDER_VALIDATION} />
                </div>
                <div className="actions form-actions dob">
                  <div className="flex-row-text hide-for-medium">
                    <Translate path="life.form.dob" />
                  </div>
                  <RequiredField name="dob" component={this.renderDob} validate={DOB_VALIDATION} />
                </div>
                <div className="actions form-actions height-weight">
                  <div className="height">
                    <div className="flex-row-text hide-for-medium">
                      <Translate path="life.form.height.label" />
                    </div>
                    <RequiredField
                      name="height"
                      component={this.renderHeightSelect}
                      normalize={convertToNumber} />
                  </div>
                  <div className="weight">
                    <div className="flex-row-text hide-for-medium">
                      <Translate path="life.form.weight" />
                    </div>
                    <RequiredField
                      name="weight"
                      component={this.renderWeight}
                      normalize={convertToNumber} />
                  </div>
                </div>
                <div className="actions form-actions household-income">
                  <div className="flex-row-text hide-for-medium">
                    <Translate path="life.form.income" />
                  </div>
                  <RequiredField name="householdIncome" component={this.renderHouseholdIncome} />
                </div>
                <div className="actions form-actions family-history">
                  <div className="flex-row-text">
                    <Translate path="life.form.familyHistory.label" />
                  </div>
                  <RequiredField
                    name="familyHistory"
                    component={this.renderFamilyHistorySegment}
                    validate={BOOLEAN_VALIDATION} />
                </div>
                <div className="actions form-actions nowrap tobacco">
                  <div className="flex-row-text">
                    <Translate path="life.form.tobacco.label" />
                  </div>
                  <RequiredField
                    name="tobacco"
                    component={this.renderSmokingSegment}
                    validate={BOOLEAN_VALIDATION} />
                </div>
                <div className="actions form-actions coverage">
                  <div className="flex-row-text hide-for-medium">
                    <Translate path="life.form.coverage.label" />
                  </div>
                  <RequiredField name="coverage" component={this.renderCoverageSelect} />
                </div>
                <div className="actions form-actions term">
                  <div className="flex-row-text hide-for-medium">
                    <Translate path="life.form.term.label" />
                  </div>
                  <RequiredField name="term" component={this.renderTermSelect} />
                </div>

                {this.renderSubmitButton()}
              </div>
            </form>
          </div>

          {isFetching && !lifeQuoteError && (
            <div className="text-center pad-bottom-3">
              <Loader />
            </div>
          )}

          {ineligible && (
            <div className="error text-center bold pad-bottom-3">
              <Translate path="life.ineligible" />
            </div>
          )}

          {lifeQuoteError && (
            <div className="error text-center bold pad-bottom-3">
              <Translate path="life.error" />
            </div>
          )}

          <Collapse isOpened={!_.isEmpty(lifeQuotes)} springConfig={{ stiffness: 60, damping: 15 }}>
            {!_.isEmpty(lifeQuotes) && (
              <div className="quoted-plans">
                <h2 className="text-center">
                  <Translate path="life.plans" />
                </h2>
                <div className="flex-row wrap center">
                  {lifeQuotes.map((quote: QuotePolicyType, idx) => {
                    const { coverage, term, monthly_premium: monthlyCost } = quote;
                    return (
                      <div className="quote-card-container" key={`${coverage}-${term}`}>
                        <PlanCard
                          status={{
                            content: `life.card.${planMapping[idx]}`,
                            classes: { className: `wrap-around ${ribbonMapping[idx]}` },
                          }}
                          isCard
                          className="white-bg"
                          style={{ margin: 0 }}>
                          <div className="medium-table v-middle quote-padding">
                            <div className="td">
                              <div>
                                <h5 className="plan-header card-padding margin-top bold">
                                  <Translate path="life.card.product" />
                                </h5>
                                <div className="coverage flex-row space-between term-details">
                                  <div className="light">
                                    <Translate path="life.card.coverage" />
                                  </div>
                                  <div className="bold">${coverage.toLocaleString()}</div>
                                </div>
                                <div className="policy-term flex-row space-between pad-top term-details">
                                  <div className="light">
                                    <Translate path="life.card.policyTerm" />
                                  </div>
                                  <div className="bold">
                                    {term}
                                    <Translate path="life.card.years" />
                                  </div>
                                </div>

                                <div className="card-checkmarks pad-y">
                                  <div className="card-checkmark">
                                    <span
                                      aria-hidden
                                      className="icon-check-mark-circle teal-color bold">
                                      {' '}
                                    </span>
                                    <span className="price bold">${monthlyCost.toFixed(2)}</span>
                                    <Translate
                                      path="life.perMonth"
                                      className="price-follow light" />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <Button
                            target="_blank"
                            className="cta-button ghost-button quote-button card-button gtm-life-redirect"
                            clickHandler={() => this.handleQuoteClick(quote)}>
                            <Translate className="gtm-life-redirect" path="life.card.pick" />
                          </Button>
                        </PlanCard>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Collapse>
        </div>
        <div className="hero-card-container pad-y-3">
          <Card className="white-bg" highlight="green" style={{ margin: 0 }}>
            <div className="row medium-table v-middle">
              <div className="td medium-12 pad-left">
                <div className="grey-2-color pad-top-small-only">
                  {_.isEmpty(lifeQuotes) && (
                    <div className="life-card-ribbon-container">
                      <Ribbon
                        classes={{ className: 'purple' }}
                        wrapAround
                        content="life.card.ribbon" />
                    </div>
                  )}
                  <h3 className="plan-header card-padding">
                    <Translate path="life.card.planName" />
                  </h3>
                  <h5 className="weight-600">
                    <Translate path="life.card.subheader" />
                  </h5>
                  <div className="card-checkmarks pad-y">
                    <h5 className="weight-600 margin-bottom-half">
                      <Translate path="life.card.advantages" />
                    </h5>
                    {['sub1', 'sub2', 'sub3'].map(key => (
                      <div className="card-checkmark" key={key}>
                        <span
                          aria-hidden
                          className="icon-check-mark-circle smaller-circle green-color bold">
                          {' '}
                        </span>
                        <Translate path={`life.card.${key}`} />
                      </div>
                    ))}
                  </div>
                  <div className="mini grey-3-color">
                    <Translate path="life.card.priceDisclaimer" />
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {benefitsSection}
      </div>
    );
  }
}

export default LifeForm;
